import type { Event } from '@bugsnag/js'

/**
 * Object keys whose values Bugsnag replaces with `[REDACTED]`, at any depth of
 * the event metadata, breadcrumb metadata and request. Passed to Bugsnag as
 * `redactedKeys`.
 */
export const redactedKeys = [
  // Bugsnag's default
  'password',
  // Credentials, wherever they end up. appLogStrategy attaches no request
  // headers or bodies, so these are a backstop
  /authorization/i,
  /cookie/i,
  /token/i,
  /secret/i,
  /api-?key/i,
  'pin',
  // Request and response bodies, which can hold customer or payment data
  'data',
  'body',
  // One-time codes in password reset and email verification links
  'oobCode',
  // Adyen's 3-D Secure redirect result
  'redirectResult',
  // Card fields in Adyen's payment state data (encryptedCardNumber,
  // encryptedSecurityCode, holderName, …)
  /^encrypted/i,
  /cardNumber|securityCode|cvc|cvv|expiry|holderName/i,
]

/** An absolute URL or a path from the site root, without whitespace */
const urlPattern = /^(?:[a-z][a-z\d+.-]*:\/\/|\/)\S*$/i

const queryValuePattern = /([?&][^=&#]+)=[^&#]*/g

const fragmentPattern = /#.+$/

/**
 * Replaces the values of a URL's query parameters and its fragment with
 * `[REDACTED]`, keeping the parameter names. Other strings are returned as is.
 * @example redactUrl('/checkout/result?redirectResult=Ab1') // '/checkout/result?redirectResult=[REDACTED]'
 */
export function redactUrl(value: string) {
  if (!urlPattern.test(value)) {
    return value
  }

  return value
    .replace(queryValuePattern, '$1=[REDACTED]')
    .replace(fragmentPattern, '#[REDACTED]')
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value == null || typeof value !== 'object') {
    return false
  }

  const prototype = Object.getPrototypeOf(value)

  return prototype === Object.prototype || prototype === null
}

/** Redacts the URLs among the strings of plain objects and arrays, at any depth */
function redactUrls(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactUrl(value)
  }

  if (Array.isArray(value)) {
    return value.map(redactUrls)
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, redactUrls(entry)]),
    )
  }

  return value
}

/**
 * Bugsnag `onError` callback that keeps URL parameters out of the report.
 * Payment and sign-in pages carry secrets in their query string (Adyen's
 * `redirectResult`, Firebase's `oobCode`), which would otherwise reach Bugsnag
 * through the page URL, navigation breadcrumbs (including the router state)
 * and network breadcrumbs.
 */
export default function redactBugsnagEvent(event: Event) {
  if (event.request?.url) {
    event.request.url = redactUrl(event.request.url)
  }

  for (const breadcrumb of event.breadcrumbs) {
    breadcrumb.metadata = redactUrls(breadcrumb.metadata) as Record<
      string,
      unknown
    >
  }
}
